import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
} from 'react-bootstrap';
import Select, { components } from 'react-select';
import { useNavigate } from 'react-router-dom';
import axios from '../../services/axios';
import Loading from '../../components/Loading';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useNotification } from '../../components/Notification/NotificationContext';

// --------------------------------------------------------------
// Componente CustomValueContainer (sem alterações)
// --------------------------------------------------------------
function CustomValueContainer(props) {
  const { children, selectProps, ...rest } = props;
  const allOptions = selectProps.options;

  return (
    <components.ValueContainer {...rest} selectProps={selectProps}>
      {children}
      {Array.isArray(allOptions) && allOptions.length > 0 && (
        <button
          type="button"
          onClick={() => {
            selectProps.onChange(allOptions, { action: 'select-option' });
          }}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: '0 4px',
            marginLeft: '4px',
            color: '#495057',
            fontWeight: 'bold',
          }}
          title="Selecionar todos"
        >
          »»
        </button>
      )}
    </components.ValueContainer>
  );
}

CustomValueContainer.propTypes = {
  children: PropTypes.node,
  selectProps: PropTypes.shape({
    options: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        label: PropTypes.string.isRequired,
      })
    ).isRequired,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        label: PropTypes.string.isRequired,
      })
    ),
  }).isRequired,
};

CustomValueContainer.defaultProps = {
  children: null,
};

export default function ServiceDeskCreate() {
  const navigate = useNavigate();
  const notification = useNotification();

  // Estados para campos de texto
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');

  // Arrays com todas as opções do backend
  const [customers, setCustomers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [stages, setStages] = useState([]);

  // Arrays de itens selecionados
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState([]);
  const [selectedPriorityIds, setSelectedPriorityIds] = useState([]);
  const [selectedStageIds, setSelectedStageIds] = useState([]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchAllOptions() {
      setLoading(true);
      try {
        const [
          customersResponse,
          departmentsResponse,
          prioritiesResponse,
          stagesResponse,
        ] = await Promise.all([
          // ✅ CORREÇÃO: Usando a nova rota otimizada que retorna a lista completa.
          axios.get('/customers/list'),
          axios.get('/departaments'),
          axios.get('/priorities'),
          axios.get('/stages'),
        ]);

        // A rota /customers/list retorna um array direto, então response.data é o que precisamos.
        setCustomers(customersResponse.data || []);
        setDepartments(departmentsResponse.data || []);
        setPriorities(prioritiesResponse.data || []);
        setStages(stagesResponse.data || []);
      } catch (error) {
        console.error('Falha ao buscar dados auxiliares:', error);
        notification(
          'Não foi possível carregar os dados para os formulários.',
          'error'
        );
      } finally {
        setLoading(false);
      }
    }

    fetchAllOptions();
  }, [notification]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post('/servicedesks', {
        name,
        displayName,
        description,
        customerIds: selectedCustomerIds.map((opt) => opt.value),
        departmentIds: selectedDepartmentIds.map((opt) => opt.value),
        priorityIds: selectedPriorityIds.map((opt) => opt.value),
        stageIds: selectedStageIds.map((opt) => opt.value),
      });

      notification('Mesa de serviço criada com sucesso.', 'success');
      navigate('/servicedesk');
    } catch (error) {
      console.error('Erro ao criar Mesa de Serviço:', error);
      notification(
        <div>
          Falha ao criar Mesa de Serviço:{' '}
          <b>
            {(error.response &&
              error.response.data &&
              error.response.data.message) ||
              error.message}
          </b>
        </div>,
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="px-lg-4 px-xl-15">
      <Breadcrumbs
        title="Mesas de Serviço"
        pages={[{ name: 'Mesa de Serviço', link: '/servicedesk' }]}
      />

      <Card className="mb-5">
        <Card.Body>
          {loading && <Loading />}

          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col>
                <Form.Group controlId="sdName">
                  <Form.Label>Nome</Form.Label>
                  <Form.Control
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="sdDisplayName">
                  <Form.Label>Display Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <Form.Group controlId="sdDescription">
                  <Form.Label>Descrição</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <Form.Group controlId="sdCustomers">
                  <Form.Label>Clientes</Form.Label>
                  <Select
                    isMulti
                    components={{ ValueContainer: CustomValueContainer }}
                    value={selectedCustomerIds}
                    onChange={setSelectedCustomerIds}
                    options={customers.map((c) => ({
                      value: c.id,
                      label: c.razaoSocial,
                    }))}
                    placeholder="Selecione clientes..."
                    isLoading={loading}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group controlId="sdDepartments">
                  <Form.Label>Departamentos</Form.Label>
                  <Select
                    isMulti
                    components={{ ValueContainer: CustomValueContainer }}
                    value={selectedDepartmentIds}
                    onChange={setSelectedDepartmentIds}
                    options={departments.map((d) => ({
                      value: d.id,
                      label: d.name,
                    }))}
                    placeholder="Selecione departamentos..."
                    isLoading={loading}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="sdPriorities">
                  <Form.Label>Prioridades</Form.Label>
                  <Select
                    isMulti
                    components={{ ValueContainer: CustomValueContainer }}
                    value={selectedPriorityIds}
                    onChange={setSelectedPriorityIds}
                    options={priorities.map((p) => ({
                      value: p.id,
                      label: p.name,
                    }))}
                    placeholder="Selecione prioridades..."
                    isLoading={loading}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="sdStages">
                  <Form.Label>Estágios</Form.Label>
                  <Select
                    isMulti
                    components={{ ValueContainer: CustomValueContainer }}
                    value={selectedStageIds}
                    onChange={setSelectedStageIds}
                    options={stages.map((s) => ({
                      value: s.id,
                      label: s.name,
                    }))}
                    placeholder="Selecione estágios..."
                    isLoading={loading}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3 justify-content-end">
              <Col xs="auto">
                <Button type="submit" variant="primary" disabled={loading}>
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    'Criar Mesa De Serviço'
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}
